import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert';
import useInvoice from 'hooks/invoice/useInvoice';
import useVoidInvoice from 'hooks/invoice/useVoidInvoice';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import notificationHandler from 'utils/notifications';

import InvoiceSummaryCard from './InvoiceSummaryCard/InvoiceSummaryCard';

interface InvoiceVoidModalProps {
  invoiceId: string;
  opened: boolean;
  close: () => void;
}

const InvoiceVoidModal = ({ invoiceId, opened, close }: InvoiceVoidModalProps): JSX.Element => {
  const staffTracking = useStaffTracking();
  const voidInvoiceMutation = useVoidInvoice();

  const { errorNotification, successNotification } = notificationHandler();

  const { data, isLoading, isSuccess } = useInvoice(invoiceId);

  const handleVoid = (): void => {
    if (data?.data) {
      voidInvoiceMutation.mutate(invoiceId, {
        onError: error => {
          errorNotification('Invoice void failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Invoice void was successful');

          staffTracking.trackInvoiceVoided(
            data?.data.invoiceId,
            data?.data.athlete.athleteId,
            data?.data.total / 100,
          );

          close();
        },
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<Title order={4}>Void Invoice</Title>}
      trapFocus
      centered
      size='lg'
    >
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      <Stack px='md' w='100%' align='center'>
        {isSuccess && data?.data && <InvoiceSummaryCard invoice={data.data} />}
        <Text fw={500} py='sm'>
          Are you sure you want to void this invoice?
        </Text>
        <KiloAlert>You can reopen this invoice at any time.</KiloAlert>
      </Stack>
      <Group position='right' pt='md'>
        <Button
          onClick={handleVoid}
          color={'yellow'}
          disabled={isLoading}
          loading={voidInvoiceMutation.isLoading}
        >
          Void
        </Button>
      </Group>
    </Modal>
  );
};

export default InvoiceVoidModal;
